import React, { useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { FileCopyOutlined } from "@material-ui/icons";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Tooltip } from "@material-ui/core";

import { i18n } from "../../translate/i18n";
import JidUtils from "../../utils/classes/JidUtils";

const useStyles = makeStyles(theme => ({
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "start",
    justifyContent: "center",
    gap: "1em",
  },

  infoContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "start",
    justifyContent: "center",
    width: "100%",
  },

  floatingButton: {
		transition: "transform 0.3s",
		"&:hover": { transform: "translateY(-5px)", },
	},
}));

const TicketPropertiesModal = ({ open, onClose, ticket }) => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();

  const timeoutCopyRef = useRef(null);
  
  const propertiesInitialValues = useMemo(() => {
    return {
      ticketId: "-"
      , ticketStatus: "-"
      , ticketProtocol: "-"
      , contactName: "-"
      , contactJid: "-"
      , isContactJidLid: false
      , userName: "-"
      , queueName: "-"
      , subqueueName: "-"
      , whatsappName: "-"
    };
  }, []);

  const [ticketProperties, setTicketProperties] = useState(propertiesInitialValues);
  const [copyTooltip, setCopyTooltip] = useState(i18n.t("messagePropertiesModal.tooltips.copy"));



  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    if (!open) setTicketProperties(propertiesInitialValues);

    let isContactJidLid = false;
    if (ticket?.contact?.number) isContactJidLid = false;
    else if (ticket?.contact?.lid) isContactJidLid = true;

    setTicketProperties({
      ticketId: ticket?.id || "-"
      , ticketStatus: ticket?.status || "-"
      , ticketProtocol: ticket?.protocol || "-"
      , contactName: ticket?.contact?.name || ticket?.noteName || "-"
      , contactJid: ticket?.contact?.number || ticket?.contact?.lid || "-"
      , isContactJidLid
      , userName: ticket?.user?.name || "-"
      , queueName: ticket?.queue?.name || "-"
      , subqueueName: ticket?.subqueue?.name || "-"
      , whatsappName: ticket?.whatsapp?.name || "-"
    });
  }, [open, ticket, propertiesInitialValues]);



  //  ***************
  //  ** Functions **
  //  ***************
  const handleClose = () => {
    onClose();
  };

  const handleCopyProperties = async () => {
    const clipboardText = `${i18n.t("ticketPropertiesModal.spans.ticketId")}: ${ticketProperties.ticketId}`
      + `\n${i18n.t("ticketPropertiesModal.spans.ticketStatus")}: ${i18n.t("ticketPropertiesModal.spans.status." + ticketProperties.ticketStatus)}`
      + `\n${i18n.t("ticketPropertiesModal.spans.ticketProtocol")}: ${ticketProperties.ticketProtocol}`
      + "\n----"
      + `\n${i18n.t("ticketPropertiesModal.spans.contactName")}: ${ticketProperties.contactName}`
      + `\n${i18n.t("ticketPropertiesModal.spans.contactJid")}: ${ticketProperties.isContactJidLid ? JidUtils.formatJid(ticketProperties.contactJid, false, ticketProperties.isContactJidLid): ticketProperties.contactJid}`
      + "\n----"
      + `\n${i18n.t("ticketPropertiesModal.spans.userName")}: ${ticketProperties.userName}`
      + "\n----"
      + `\n${i18n.t("ticketPropertiesModal.spans.queueName")}: ${ticketProperties.queueName}`
      + `\n${i18n.t("ticketPropertiesModal.spans.subqueueName")}: ${ticketProperties.subqueueName}`
      + "\n----"
      + `\n${i18n.t("ticketPropertiesModal.spans.whatsappName")}: ${ticketProperties.whatsappName}`;

    await navigator.clipboard.writeText(clipboardText);
    setCopyTooltip(i18n.t("messagePropertiesModal.tooltips.copied"));

    if (timeoutCopyRef.current) clearTimeout(timeoutCopyRef.current);
    timeoutCopyRef.current = setTimeout(() => setCopyTooltip(i18n.t("messagePropertiesModal.tooltips.copy")), 700);
  };



  //  ************
  //  ** Return **
  //  ************
  if (!open) return (<></>);

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" scroll="paper" fullWidth>
        <DialogTitle>
          <span>{i18n.t("ticketPropertiesModal.title.up")}</span>
        </DialogTitle>

        <DialogContent className={classes.dialog} dividers>
          <div className={classes.infoContainer}>
            <span><b>{i18n.t("ticketPropertiesModal.spans.ticketId")}: </b>{ticketProperties.ticketId}</span><br />
            <span><b>{i18n.t("ticketPropertiesModal.spans.ticketStatus")}: </b>{i18n.t(`ticketPropertiesModal.spans.status.${ticketProperties.ticketStatus}`)}</span><br />
            <span><b>{i18n.t("ticketPropertiesModal.spans.ticketProtocol")}: </b>{ticketProperties.ticketProtocol}</span><br />
            <span><b>{i18n.t("ticketPropertiesModal.spans.contactName")}: </b>{ticketProperties.contactName}</span><br />
            <span><b>{i18n.t("ticketPropertiesModal.spans.contactJid")}: </b>{ticketProperties.isContactJidLid ? JidUtils.formatJid(ticketProperties.contactJid, false, ticketProperties.isContactJidLid): ticketProperties.contactJid}</span><br />
            <span><b>{i18n.t("ticketPropertiesModal.spans.userName")}: </b>{ticketProperties.userName}</span><br />
            <span><b>{i18n.t("ticketPropertiesModal.spans.queueName")}: </b>{ticketProperties.queueName}</span><br />
            <span><b>{i18n.t("ticketPropertiesModal.spans.subqueueName")}: </b>{ticketProperties.subqueueName}</span><br />
            <span><b>{i18n.t("ticketPropertiesModal.spans.whatsappName")}: </b>{ticketProperties.whatsappName}</span>
          </div>
        </DialogContent>

        <Divider />

        <DialogActions>
          <Button
            onClick={handleClose}
            color="inherit"
            variant="outlined"
            className={classes.floatingButton}
          >
            {i18n.t("ticketPropertiesModal.buttons.cancel")}
          </Button>

          <Tooltip title={copyTooltip} placement="top-start" arrow>
            <Button
              color="inherit"
              variant="outlined"
              className={classes.floatingButton}
              onClick={async () => await handleCopyProperties()}
            >
              <FileCopyOutlined />
            </Button>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TicketPropertiesModal;
